import { useCallback, useEffect, useMemo, useState } from "react";
import { apiError } from "@/lib/api";
import { loadStore, toPayload } from "@/lib/load/store";

const ACTIVE_KEY = "fhp-load-active-trailer";

export function useLoadData(user, say) {
  const store = useMemo(() => loadStore(!!user), [user]);
  const [trailers, setTrailers] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [activeId, setActiveId] = useState(localStorage.getItem(ACTIVE_KEY));
  const fail = useCallback((e) => say(apiError(e) || "Couldn't save. Check your signal and try again.", "danger"), [say]);

  useEffect(() => {
    if (user === null) return;
    store.trailers.list().then(setTrailers).catch((e) => { setTrailers([]); fail(e); });
    store.customers.list().then(setCustomers).catch(fail);
  }, [store, user, fail]);

  const trailer = trailers?.find((x) => x.id === activeId) || trailers?.[0] || null;
  useEffect(() => { if (trailer) localStorage.setItem(ACTIVE_KEY, trailer.id); }, [trailer]);

  const saveTrailer = async (data, isNew) => {
    try {
      if (isNew) { const x = await store.trailers.add(data); setTrailers((ts) => [...ts, x]); setActiveId(x.id); }
      else { const x = await store.trailers.update(trailer.id, data); setTrailers((ts) => ts.map((y) => (y.id === x.id ? x : y))); }
      say("Trailer saved", "safe"); return true;
    } catch (e) { fail(e); return false; }
  };
  const removeTrailer = async () => {
    try { await store.trailers.remove(trailer.id); setTrailers((ts) => ts.filter((x) => x.id !== trailer.id)); setActiveId(null); say("Trailer deleted", "safe"); return true; }
    catch (e) { fail(e); return false; }
  };
  const toggleDry = async (i) => {
    const next = { ...trailer, compartments: trailer.compartments.map((c, j) => (j === i ? { ...c, locked_dry: !c.locked_dry } : c)) };
    setTrailers((ts) => ts.map((x) => (x.id === next.id ? next : x)));
    try { await store.trailers.update(trailer.id, toPayload(next)); }
    catch (e) { setTrailers((ts) => ts.map((x) => (x.id === trailer.id ? trailer : x))); fail(e); }
  };
  const saveCustomer = async (data, id) => {
    try {
      const c = id ? await store.customers.update(id, data) : await store.customers.add(data);
      setCustomers((cs) => (id ? cs.map((x) => (x.id === c.id ? c : x)) : [...cs, c]));
      say("Customer saved", "safe"); return c;
    } catch (e) { fail(e); return null; }
  };
  const removeCustomer = async (id) => {
    try { await store.customers.remove(id); setCustomers((cs) => cs.filter((c) => c.id !== id)); say("Customer deleted", "safe"); return true; }
    catch (e) { fail(e); return false; }
  };
  return { trailers, trailer, setActiveId, customers, saveTrailer, removeTrailer, toggleDry, saveCustomer, removeCustomer };
}
